import React, { useState } from 'react';
import { Upload, CheckCircle, XCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImportPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [importKey, setImportKey] = useState('');
  const [skipExisting, setSkipExisting] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.json')) {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Veuillez sélectionner un fichier JSON');
      setFile(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Veuillez sélectionner un fichier');
      return;
    }
    if (!importKey) {
      setError('Veuillez entrer la clé d\'import');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('import_key', importKey);
      formData.append('skip_existing', skipExisting.toString());

      const response = await fetch(`${API}/offers/import`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'import');
      }

      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={20} />
            Retour au dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Import des offres</h1>
          <p className="text-gray-600 mt-2">
            Importez vos offres depuis un fichier JSON exporté
          </p>
        </div>

        {/* Import Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fichier JSON à importer
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
              <input
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                {file ? (
                  <p className="text-green-600 font-medium">{file.name}</p>
                ) : (
                  <>
                    <p className="text-gray-600">Cliquez pour sélectionner un fichier</p>
                    <p className="text-sm text-gray-400 mt-1">Format accepté : .json</p>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Import Key */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clé d'import (sécurité)
            </label>
            <input
              type="password"
              value={importKey}
              onChange={(e) => setImportKey(e.target.value)}
              placeholder="Entrez la clé d'import"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Contactez votre administrateur si vous ne connaissez pas la clé
            </p>
          </div>

          {/* Options */}
          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={skipExisting}
                onChange={(e) => setSkipExisting(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Ignorer les offres qui existent déjà (recommandé)
              </span>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <XCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-red-800 font-medium">Erreur</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Success Result */}
          {result && (
            <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
              result.success ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
            }`}>
              {result.success ? (
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
              ) : (
                <AlertCircle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
              )}
              <div>
                <p className={`font-medium ${result.success ? 'text-green-800' : 'text-yellow-800'}`}>
                  Import terminé
                </p>
                <div className="text-sm mt-2 space-y-1">
                  <p className="text-gray-600">
                    <span className="font-medium">{result.total_offers}</span> offres dans le fichier
                  </p>
                  <p className="text-green-600">
                    <span className="font-medium">{result.imported}</span> offres importées
                  </p>
                  {result.skipped > 0 && (
                    <p className="text-gray-500">
                      <span className="font-medium">{result.skipped}</span> offres ignorées (déjà existantes)
                    </p>
                  )}
                  {result.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="text-red-600 font-medium">Erreurs :</p>
                      <ul className="list-disc list-inside text-red-500 text-xs">
                        {result.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleImport}
            disabled={loading || !file}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              loading || !file
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Import en cours...
              </span>
            ) : (
              'Importer les offres'
            )}
          </button>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="font-medium text-blue-900 mb-3">Comment ça marche ?</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
            <li>
              <strong>Exportez</strong> vos offres depuis l'environnement source en allant sur :<br/>
              <code className="bg-blue-100 px-2 py-0.5 rounded text-xs mt-1 inline-block">
                /api/offers/export?export_key=VOTRE_CLE
              </code>
            </li>
            <li>
              <strong>Téléchargez</strong> le fichier JSON généré
            </li>
            <li>
              <strong>Uploadez</strong> ce fichier sur cette page
            </li>
            <li>
              <strong>Entrez</strong> la clé d'import et cliquez sur "Importer"
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ImportPage;
